import React from 'react';
import parse from 'html-react-parser';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import createOpenGraphTags from '@rsc/utils/createOpenGraphTags';
import Layout from '@components/layout';
import { styles } from '@components/utils';
import PageWrapper from '@rsc/ui-components/page-wrapper/PageWrapper';
import KurgoContainer from '@components/utils/ContainerStyles';
import Breadcrumb from '@components/breadcrumb';
import searchForBreadcrumbsInNavigation from '@components/utils/searchForBreadcrumbsInNavigation';

const Container = styled.div`
  // Resource Center Page
  .bullet-style {
    ul {
      margin: 0;
      padding: 0 0 0 20px;
    }
    li a {
      font-size: ${styles.fontSize.base};
      line-height: 18px;
      color: ${styles.colors.gray[500]};
      &:hover {
        text-decoration: underline;
      }
    }
    li {
      list-style: none;
      position: relative;
      padding: 6px 0 6px 25px;
    }
    li::before {
      content: '';
      position: absolute;
      background: url('/images/bullet-style.svg');
      background-repeat: no-repeat;
      background-size: 100%;
      width: 16px;
      height: 16px;
      left: 0;
      top: 10px;
    }
  }

  // In-The-Media page
  .logo-image {
    border-radius: 50%;
    background: #fff;
    width: 87px;
    height: 87px;
    display: flex;
    box-shadow: 0 0 30px 0 rgb(153 153 153 / 20%);
    overflow: hidden;
    margin: auto;
  }

  .orange-hover {
    a {
      color: black;
    }
    a:hover {
      color: ${styles.colors.orange[500]};
    }
  }

  li {
    margin-bottom: 0.5em;
  }

  li a {
    margin-bottom: 0.5em;
  }

  p {
    line-height: 1.5;
  }

  img {
    overflow: hidden;
    max-width: 100%;
  }
`;

const MarketingPage = ({ pageContext, data }) => {
  const { body, title, seo, handle } = pageContext;

  const shopifyMainMenu = data.shopifyMainMenu.items;

  let bodyReact;

  const breadcrumbs = searchForBreadcrumbsInNavigation(shopifyMainMenu, title);

  // inject shogun scripts into document head
  // this is only needed for shogun javascript
  // to work in gatsby develop mode
  if (typeof window !== 'undefined') {
    bodyReact = parse(body, {
      replace: domNode => {
        if (domNode.type === 'script') {
          if (domNode.attribs.src) {
            const script = document.createElement('script');
            script.src = domNode.attribs.src;
            document.head.appendChild(script);
          }
          // Load storemapper scripts in gatsby develop mode
          if (domNode.attribs['data-storemapper-id']) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = 'https://www.storemapper.co/js/widget.js';
            document.head.appendChild(script);
          }
        }
      },
    });
  } else {
    bodyReact = parse(body);
  }

  return (
    <Layout
      headTagData={{
        title: seo?.title || `${title} - Kurgo Dog Products`,
        description: seo.description || '',
        meta: createOpenGraphTags(
          {
            title: seo?.title || `${title} - Kurgo Dog Products`,
            description: seo?.description || '',
            url: `/${handle}`,
          },
          'kurgo'
        ),
      }}
    >
      <Container>
        <KurgoContainer backgroundColor={styles.colors.gray[100]}>
          {/* here is the wrapper breadcrumbs for marketing pages */}
          {handle && <Breadcrumb elements={breadcrumbs} />}
          <PageWrapper>{bodyReact}</PageWrapper>
        </KurgoContainer>
      </Container>
    </Layout>
  );
};

export const query = graphql`
  query ShopifyMainMenuQuery {
    shopifyMainMenu {
      items {
        title
        url
        items {
          title
          url
          items {
            title
            url
          }
        }
      }
    }
  }
`;

MarketingPage.propTypes = {
  data: PropTypes.shape({
    shopifyMainMenu: PropTypes.shape({
      items: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          items: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string.isRequired,
              url: PropTypes.string.isRequired,
            })
          ),
        })
      ),
    }),
  }).isRequired,
  pageContext: PropTypes.shape({
    body: PropTypes.string,
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
    title: PropTypes.string,
    bodySummary: PropTypes.string,
    seo: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    handle: PropTypes.string,
  }).isRequired,
};

export default MarketingPage;
